import { eV1Protocol, eV1HostWithPort, TAGGED_VERSION, CURRENT_SHA } from './hosts.js';
import { reportError } from './sentryUtils.ts';
import { countMetric } from './simpleMetrics.js';

const MAX_RETRIES = 3;

const importWithRetry = async (
  baseUrl: string,
  retries = MAX_RETRIES,
  delay = 200,
  attempt = 0,
): Promise<unknown> => {
  // Append a cache-busting query parameter to avoid Safari's caching of failed imports
  const url =
    attempt === 0 ? baseUrl : `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}retry=${attempt}`;
  try {
    const result: unknown = await import(/* webpackIgnore: true */ url);
    if (attempt > 0) {
      countMetric('dynamic-import/retry-success', 1, { attempt: String(attempt), url: baseUrl });
    }
    return result;
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise<void>((resolve) => {
      setTimeout(resolve, delay);
    });
    return importWithRetry(baseUrl, retries - 1, delay, attempt + 1);
  }
};

export type DynamicImportOptions = {
  host?: string;
  mediaData?: boolean;
};
export const dynamicImport = async (
  filePath: string,
  options: DynamicImportOptions = {},
): Promise<unknown> => {
  const host: string =
    Object.hasOwn(options, 'host') && options.host != null ? options.host : eV1HostWithPort();

  const taggedVersion: string = TAGGED_VERSION;

  const currentSha: string = CURRENT_SHA;

  const getVersionSuffix = (): string => {
    if (taggedVersion !== '' && taggedVersion.length > 0 && options.mediaData !== true) {
      return `@${taggedVersion}`;
    }
    // If there is no tagged version, we can fall back to the current SHA.
    // This mechanism keeps a cached script from fetching a newer, mismatched version of an imported bundle.
    if (currentSha !== '' && currentSha.length > 0 && options.mediaData !== true) {
      return `@${currentSha}`;
    }
    return '';
  };

  const url = `${eV1Protocol()}//${host}/${filePath}${getVersionSuffix()}`;

  try {
    return await importWithRetry(url);
  } catch (error) {
    const reportedError = error instanceof Error ? error : new Error(String(error));
    reportError('dynamicImport', reportedError, {
      importUrl: url,
    });
    countMetric('dynamic-import/failure-after-retry', 1, { attempt: String(MAX_RETRIES), url });
    throw error;
  }
};
